/** 雷达图（F-020 多策略对比）。把多指标归一化到同一雷达，越外越好。 */
import { useEffect, useRef } from 'react';
import { THEME } from '../../render/theme';
import type { ExperimentResult } from '../../data/export-csv';

interface Axis {
  label: string;
  get: (r: ExperimentResult) => number;
  invert?: boolean; // 越小越好 → 反转
}

const AXES: Axis[] = [
  { label: '低等待', get: (r) => r.summary.avgWaitMs, invert: true },
  { label: '低乘梯', get: (r) => r.summary.avgRideMs, invert: true },
  { label: '吞吐', get: (r) => r.summary.throughputPerHour },
  { label: '利用率', get: (r) => r.summary.carUtilization },
  { label: '低能耗', get: (r) => r.summary.energyKwh, invert: true },
];

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#a855f7', '#06b6d4'];

interface Props {
  results: ExperimentResult[];
  height?: number;
}

export function RadarChart({ results, height = 260 }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current!;
    const ctx = canvas.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    draw(ctx, w, height, results);
  }, [results, height]);
  return <canvas ref={ref} style={{ width: '100%', height }} className="rounded-md border border-slate-800 bg-[#0f1115]" />;
}

function draw(ctx: CanvasRenderingContext2D, w: number, h: number, results: ExperimentResult[]) {
  ctx.clearRect(0, 0, w, h);
  const cx = w / 2;
  const cy = h / 2;
  const radius = Math.min(w, h) / 2 - 34;
  const n = AXES.length;

  // 各轴归一化范围
  const ranges = AXES.map((axis) => {
    const vals = results.map((r) => axis.get(r));
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    return { min, max: max === min ? min + 1 : max };
  });

  // 网格
  ctx.strokeStyle = THEME.grid;
  for (let ring = 1; ring <= 4; ring++) {
    ctx.beginPath();
    for (let i = 0; i <= n; i++) {
      const ang = (Math.PI * 2 * i) / n - Math.PI / 2;
      const r = (radius * ring) / 4;
      const x = cx + Math.cos(ang) * r;
      const y = cy + Math.sin(ang) * r;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }

  // 轴线 + 标签
  ctx.fillStyle = THEME.textDim;
  ctx.font = '11px system-ui';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (let i = 0; i < n; i++) {
    const ang = (Math.PI * 2 * i) / n - Math.PI / 2;
    ctx.strokeStyle = THEME.grid;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(ang) * radius, cy + Math.sin(ang) * radius);
    ctx.stroke();
    ctx.fillText(AXES[i]!.label, cx + Math.cos(ang) * (radius + 16), cy + Math.sin(ang) * (radius + 16));
  }

  // 每个结果画多边形
  results.forEach((res, idx) => {
    const color = COLORS[idx % COLORS.length]!;
    ctx.strokeStyle = color;
    ctx.fillStyle = color + '33';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let i = 0; i <= n; i++) {
      const ai = i % n;
      const axis = AXES[ai]!;
      const { min, max } = ranges[ai]!;
      let norm = (axis.get(res) - min) / (max - min); // 0..1
      if (axis.invert) norm = 1 - norm;
      const ang = (Math.PI * 2 * ai) / n - Math.PI / 2;
      const r = radius * Math.max(0, Math.min(1, norm));
      const x = cx + Math.cos(ang) * r;
      const y = cy + Math.sin(ang) * r;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  });
}
